import { createSlice } from "@reduxjs/toolkit";
import { createQueryAsyncThunk } from "../../../app/createApiAsyncThunk";
import { RootState } from "../../../app/store";
import { getTalentCalendarEvents, getTalentCalendarEventsMock, } from "./calendarAPI";
import { addDays, startOfDay } from "date-fns";
import { CalendarEvent, CalendarEventFilters, TalentCalendarEvent, } from "./types";
import { formatDateRangeMonthYearAware } from "../../../common/helpers/formatDateTime";

export type SliceState = {
  events: CalendarEvent[];
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

const initialState: SliceState = {
  events: [],
  status: "idle",
  error: undefined,
};

const USE_MOCK_API = false;

export const fetchCalendarEvents = createQueryAsyncThunk<
  CalendarEvent[],
  CalendarEventFilters
>("calendar/fetchEvents", async (filters, options) => {
  const queryFn = USE_MOCK_API
    ? getTalentCalendarEventsMock
    : getTalentCalendarEvents;
  const data: TalentCalendarEvent[] = await queryFn(filters, options);

  return data
    .filter((event) => event.end !== null)
    .map((event) => {
      const start = new Date(event.start);
      const end = new Date(event.end!);

      if (event.type === "UnavailabilityEntry") {
        // Full-day = starts at ~00:00 and ends at ~23:59 (or next day 00:00)
        const dayStart = startOfDay(start);
        const nextDayStart = startOfDay(addDays(dayStart, 1));
        const fiveMinutesMs = 5 * 60 * 1000;

        const isFullDay =
          start.getTime() - dayStart.getTime() <= fiveMinutesMs &&
          end.getTime() >= nextDayStart.getTime() - fiveMinutesMs;

        const endDayInclusive = startOfDay(new Date(end.getTime() - 1));
        const isSingleDay = dayStart.getTime() === endDayInclusive.getTime();
        const isToday = dayStart.getTime() === startOfDay(new Date()).getTime();

        const title = isFullDay
          ? isSingleDay
            ? isToday
              ? {
                  titleKey: "calendar.unavailability.today" as const,
                }
              : {
                  titleKey: "calendar.unavailability.singleDay" as const,
                }
            : {
                titleKey: "calendar.unavailability.range" as const,
                titleParams: {
                  dateRange: formatDateRangeMonthYearAware(
                    dayStart,
                    endDayInclusive,
                  ),
                },
              }
          : {
              titleKey: "calendar.unavailability.singleDay" as const,
            };

        const calendarEvent: CalendarEvent = {
          id: event.id,
          start,
          end,
          type: event.type,
          titleKey: title.titleKey,
          titleParams: title.titleParams,
          unavailabilityType: isFullDay ? "fullDay" : "partialDay",
        };
        return calendarEvent;
      }

      const calendarEvent: CalendarEvent = {
        id: event.id,
        start,
        end,
        type: event.type,
        titleKey: "calendar.worktime",
      };
      return calendarEvent;
    });
});

const calendarSlice = createSlice({
  name: "calendar",
  initialState,
  reducers: {
    clearEvents: (state) => {
      state.events = [];
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCalendarEvents.pending, (state) => {
        state.status = "pending";
        state.error = undefined;
      })
      .addCase(fetchCalendarEvents.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.events = action.payload;
      })
      .addCase(fetchCalendarEvents.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  },
});

export const { clearEvents } = calendarSlice.actions;

// Selectors
export const selectCalendarEvents = (state: RootState) => state.calendar.events;
export const selectCalendarStatus = (state: RootState) => state.calendar.status;
export const selectCalendarError = (state: RootState) => state.calendar.error;

export const selectIsLoading = (state: RootState) =>
  state.calendar.status === "pending";

export default calendarSlice.reducer;
